import React, { Component } from "react";

import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import Container from "@cloudscape-design/components/container";
import FormField from "@cloudscape-design/components/form-field";
import Grid from "@cloudscape-design/components/grid";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Pagination from "@cloudscape-design/components/pagination";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Table from "@cloudscape-design/components/table";

const MAX_SIGNED_INT = 2147483647;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 30, 50, 100].map((value) => ({ label: String(value), value: String(value) }));
const STATUS_OPTIONS = [
	{ label: "All statuses", value: "" },
	{ label: "Pending deposit", value: "pending_deposit" },
	{ label: "Deposit qualified", value: "deposit_qualified" },
	{ label: "Granting", value: "granting" },
	{ label: "Granted", value: "granted" },
	{ label: "Grant failed", value: "grant_failed" },
	{ label: "Rejected antifraud", value: "rejected_antifraud" },
	{ label: "Rejected: duplicate IP", value: "rejected_registration_duplicate_ip" },
	{ label: "Cancelled disabled", value: "cancelled_disabled" },
];
const STATUS_LABELS = new Map(STATUS_OPTIONS.map((option) => [option.value, option.label]));

const parseUid = (value) => {
	const rawValue = String(value).trim();
	if (!/^\d+$/.test(rawValue)) return null;

	const parsedValue = parseInt(rawValue, 10);
	return Number.isSafeInteger(parsedValue) && parsedValue > 0 && parsedValue <= MAX_SIGNED_INT ? parsedValue : null;
};

const getErrorMessage = (error) => {
	if (typeof error === "string") return error;
	if (typeof error?.text === "string") return error.text;
	if (typeof error?.message === "string") return error.message;
	return "Unable to load Welcome Freespins statistics.";
};

const formatDate = (value) => {
	if (value === null || typeof value === "undefined") return "-";
	const date = new Date(value);
	return Number.isFinite(date.getTime()) ? date.toLocaleString() : String(value);
};

class WelcomeFreespinsStatisticsTab extends Component {
	constructor(props) {
		super(props);

		this.requestId = 0;
		this.unmounted = false;
		this.uidFilterTimer = null;
		this.hasCachedState =
			Boolean(props.cache?.state) && props.cache.partnerUid === props.partnerUid && props.cache.currentVersion === props.currentVersion;
		this.state = this.hasCachedState ? props.cache.state : {
			data: null,
			availableVersions: [props.currentVersion],
			selectedVersion: props.currentVersion,
			limit: DEFAULT_PAGE_SIZE,
			offset: 0,
			uidInput: "",
			uidFilter: null,
			uidError: "",
			status: "",
			loading: true,
			error: "",
		};
	}

	componentDidMount() {
		if (!this.hasCachedState) this.loadStatistics();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.partnerUid !== this.props.partnerUid || prevProps.currentVersion !== this.props.currentVersion) {
			if (this.uidFilterTimer) clearTimeout(this.uidFilterTimer);
			this.requestId += 1;
			if (this.props.cache) {
				delete this.props.cache.state;
				delete this.props.cache.partnerUid;
				delete this.props.cache.currentVersion;
			}
			this.setState(
				{
					data: null,
					availableVersions: [this.props.currentVersion],
					selectedVersion: this.props.currentVersion,
					limit: DEFAULT_PAGE_SIZE,
					offset: 0,
					uidInput: "",
					uidFilter: null,
					uidError: "",
					status: "",
					loading: true,
					error: "",
				},
				this.loadStatistics,
			);
		}
	}

	componentWillUnmount() {
		if (this.uidFilterTimer) clearTimeout(this.uidFilterTimer);
		if (this.props.cache) {
			if (this.state.loading) delete this.props.cache.state;
			else {
				this.props.cache.state = this.state;
				this.props.cache.partnerUid = this.props.partnerUid;
				this.props.cache.currentVersion = this.props.currentVersion;
			}
		}
		this.unmounted = true;
		this.requestId += 1;
	}

	loadStatistics = async (overrides = {}) => {
		const selectedVersion = Object.prototype.hasOwnProperty.call(overrides, "selectedVersion") ? overrides.selectedVersion : this.state.selectedVersion;
		const limit = Object.prototype.hasOwnProperty.call(overrides, "limit") ? overrides.limit : this.state.limit;
		const offset = Object.prototype.hasOwnProperty.call(overrides, "offset") ? overrides.offset : this.state.offset;
		const uidFilter = Object.prototype.hasOwnProperty.call(overrides, "uidFilter") ? overrides.uidFilter : this.state.uidFilter;
		const status = Object.prototype.hasOwnProperty.call(overrides, "status") ? overrides.status : this.state.status;
		const requestId = ++this.requestId;
		const payload = {
			partnerUid: this.props.partnerUid,
			settingVersion: selectedVersion,
			limit,
			offset,
		};
		if (uidFilter !== null) payload.uid = uidFilter;
		if (status) payload.status = status;

		this.setState({ data: null, selectedVersion, limit, offset, uidFilter, status, loading: true, error: "" });

		try {
			const response = await this.props.Interface.welcomeFreespinsSettingsGetStatistics(payload);
			if (this.unmounted || requestId !== this.requestId) return;
			if (!response || !Array.isArray(response.availableVersions) || !Array.isArray(response.items) || !response.summary) {
				throw new Error("invalid_statistics_response");
			}

			if (response.total > 0 && offset >= response.total) {
				const lastOffset = Math.floor((response.total - 1) / limit) * limit;
				this.loadStatistics({ selectedVersion, limit, offset: lastOffset, uidFilter, status });
				return;
			}

			this.setState({
				data: response,
				availableVersions: response.availableVersions,
				selectedVersion: response.selectedVersion,
				offset: response.total === 0 ? 0 : response.offset,
				loading: false,
				error: "",
			});
		} catch (error) {
			if (this.unmounted || requestId !== this.requestId) return;
			this.setState({ data: null, loading: false, error: getErrorMessage(error) });
		}
	};

	applyUidFilter = () => {
		if (this.uidFilterTimer) clearTimeout(this.uidFilterTimer);
		const rawValue = this.state.uidInput.trim();
		if (!rawValue) {
			this.setState({ uidError: "" });
			this.loadStatistics({ offset: 0, uidFilter: null });
			return;
		}

		const uid = parseUid(rawValue);
		if (!uid) {
			this.setState({ uidError: "Enter one positive player UID." });
			return;
		}

		this.setState({ uidError: "" });
		this.loadStatistics({ offset: 0, uidFilter: uid });
	};

	updateUidFilter = (uidInput) => {
		if (this.uidFilterTimer) clearTimeout(this.uidFilterTimer);
		this.requestId += 1;
		this.setState({ uidInput, uidError: "", loading: false });
		this.uidFilterTimer = setTimeout(this.applyUidFilter, 400);
	};

	renderCounter(label, value) {
		return (
			<Box>
				<Box variant="awsui-key-label">{label}</Box>
				<Box fontSize="heading-m">{value}</Box>
			</Box>
		);
	}

	renderSummary() {
		const { data } = this.state;
		if (!data) return null;
		const { summary } = data;

		return (
			<SpaceBetween size="m">
				<Container header={<Header variant="h3">Summary</Header>}>
					<ColumnLayout columns={6} variant="text-grid">
						{this.renderCounter("Cap maximum", data.capMaximum === null ? "Not available" : data.capMaximum)}
						{this.renderCounter("Cap used", summary.capUsed)}
						{this.renderCounter("Granted", summary.grantedCount)}
						{this.renderCounter("Modal shown", summary.modalShownCount)}
						{this.renderCounter("Activation shown", summary.activationShownCount)}
						{this.renderCounter("Duplicate IP rejections", summary.registrationDuplicateRejectedCount)}
					</ColumnLayout>
				</Container>
				<Container header={<Header variant="h3">Records by status</Header>}>
					<ColumnLayout columns={4} variant="text-grid">
						{STATUS_OPTIONS.slice(1).map((option) => (
							<React.Fragment key={option.value}>{this.renderCounter(option.label, summary.byStatus[option.value])}</React.Fragment>
						))}
					</ColumnLayout>
				</Container>
			</SpaceBetween>
		);
	}

	render() {
		const { data, availableVersions, selectedVersion, limit, offset, uidInput, uidError, status, loading, error } = this.state;
		const versionOptions = availableVersions.map((version) => ({ label: `Version ${version}`, value: String(version) }));
		const selectedVersionOption = versionOptions.find((option) => option.value === String(selectedVersion)) || null;
		const selectedStatusOption = STATUS_OPTIONS.find((option) => option.value === status);
		const selectedPageSizeOption = PAGE_SIZE_OPTIONS.find((option) => option.value === String(limit));
		const total = data?.total || 0;
		const currentPage = Math.floor(offset / limit) + 1;
		const pagesCount = Math.max(1, Math.ceil(total / limit));

		return (
			<SpaceBetween size="m">
				{error ? (
					<Alert type="error" header="Unable to load statistics" action={<Button onClick={() => this.loadStatistics()}>Retry</Button>}>
						{error}
					</Alert>
				) : null}

				<Grid gridDefinition={[{ colspan: 3 }, { colspan: 4 }, { colspan: 3 }, { colspan: 2 }]}>
					<FormField label="Exact version">
						<Select
							selectedOption={selectedVersionOption}
							options={versionOptions}
							onChange={({ detail }) => this.loadStatistics({ selectedVersion: parseInt(detail.selectedOption.value, 10), offset: 0 })}
							disabled={loading}
						/>
					</FormField>
					<FormField label="Exact player UID" errorText={uidError}>
						<Input
							type="search"
							value={uidInput}
							inputMode="numeric"
							placeholder="Player UID"
							onChange={({ detail }) => this.updateUidFilter(detail.value)}
							onKeyDown={({ detail }) => detail.key === "Enter" && this.applyUidFilter()}
						/>
					</FormField>
					<FormField label="Exact status">
						<Select
							selectedOption={selectedStatusOption}
							options={STATUS_OPTIONS}
							onChange={({ detail }) => this.loadStatistics({ status: detail.selectedOption.value, offset: 0 })}
							disabled={loading}
						/>
					</FormField>
					<FormField label="Rows per page">
						<Select
							selectedOption={selectedPageSizeOption}
							options={PAGE_SIZE_OPTIONS}
							onChange={({ detail }) => this.loadStatistics({ limit: parseInt(detail.selectedOption.value, 10), offset: 0 })}
							disabled={loading}
						/>
					</FormField>
				</Grid>

				{this.renderSummary()}

				<Table
					stickyHeader
					trackBy="uid"
					loading={loading}
					loadingText="Loading Welcome records"
					items={data?.items || []}
					columnDefinitions={[
						{ id: "uid", header: "Player UID", cell: (item) => item.uid },
						{ id: "registrationDate", header: "Registration date", cell: (item) => formatDate(item.registrationDate) },
						{ id: "status", header: "Status", cell: (item) => STATUS_LABELS.get(item.status) || item.status },
						{ id: "qualifyingDepositId", header: "Qualifying deposit ID", cell: (item) => item.qualifyingDepositId ?? "-" },
						{ id: "modalSeenAt", header: "Modal seen", cell: (item) => formatDate(item.modalSeenAt) },
						{ id: "activationSeenAt", header: "Activation seen", cell: (item) => formatDate(item.activationSeenAt) },
					]}
					header={
						<Header
							variant="h3"
							counter={`(${total})`}
							actions={
								<Button onClick={() => this.loadStatistics()} disabled={loading}>
									Refresh
								</Button>
							}>
							Welcome records
						</Header>
					}
					pagination={
						<Pagination
							disabled={loading}
							currentPageIndex={currentPage}
							pagesCount={pagesCount}
							onChange={({ detail }) => this.loadStatistics({ offset: (detail.currentPageIndex - 1) * limit })}
						/>
					}
					empty={
						<Box textAlign="center" color="inherit">
							<b>No Welcome records found</b>
							<Box variant="p" color="inherit">
								No records match the selected version and filters.
							</Box>
						</Box>
					}
				/>
			</SpaceBetween>
		);
	}
}

export default WelcomeFreespinsStatisticsTab;
